import type { BaseSelectRef } from '@rc-component/select'
import { Select } from 'antd'
import React from 'react'
import styled from 'styled-components'

import { SalesforceCard } from './SalesforceCard'

const StyledGroupLabel = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
`

export interface SalesforceDataItem {
  searchName: string
  value: string
  startDate?: string
  endDate?: string
  parent?: string
  label?: string
  children?: SalesforceDataItem[]
}

interface SelectOption {
  label: React.ReactNode
  value: string
  group?: string
}

interface GroupOption {
  label: React.ReactNode
  options: SelectOption[]
}

interface SalesforceSelectProps {
  id: string
  value?: string
  onChange: (value: string) => void
  onBlur: () => void
  data: SalesforceDataItem[]
  ref?: React.Ref<BaseSelectRef>
  showDates?: boolean
  groupByParent?: boolean
  salesforceAccountIdToNameMap?: Map<string, string>
  placeholder?: string
  style?: React.CSSProperties
  notFoundContent?: React.ReactNode
}

const StyledSelectWrapper = styled.div`
  width: 100%;

  .ant-select-selector {
    border-radius: 4px !important;
  }

  .ant-select-selector:hover {
    border-color: ${({ theme }) => theme.forecast.border2} !important;
  }

  .ant-select-focused .ant-select-selector,
  .ant-select-selector:focus,
  .ant-select-selector:active,
  .ant-select-open .ant-select-selector {
    border-color: ${({ theme }) => theme.colors.SkyBlue} !important;
    outline: 0 !important;
  }
`

export const SalesforceSelect = React.forwardRef<
  BaseSelectRef,
  SalesforceSelectProps
>(
  (
    {
      id,
      value,
      onChange,
      onBlur,
      data,
      showDates = false,
      groupByParent = false,
      salesforceAccountIdToNameMap,
      placeholder = 'Select',
      style,
      notFoundContent,
    },
    ref
  ) => {
    const options: SelectOption[] = data.map((item) => ({
      label: (
        <SalesforceCard
          key={`card-${item.value}`}
          label={item.searchName}
          id={item.value}
          startDate={showDates ? item.startDate : undefined}
          endDate={showDates ? item.endDate : undefined}
        />
      ),
      value: item.value,
      group: groupByParent ? item.parent : undefined,
    }))
    let optionsToRender: (SelectOption | GroupOption)[] = options
    if (groupByParent) {
      const groupedOptions = new Map<string, SelectOption[]>()
      const noParentOptions: SelectOption[] = []

      options.forEach((option) => {
        if (option.group) {
          if (!groupedOptions.has(option.group)) {
            groupedOptions.set(option.group, [])
          }
          groupedOptions.get(option.group)!.push(option)
        } else {
          noParentOptions.push(option)
        }
      })

      optionsToRender = Array.from(groupedOptions.entries()).map(
        ([group, groupOptions]) => ({
          label: (
            <StyledGroupLabel key={`group-${group}`}>
              Parent Org: {salesforceAccountIdToNameMap?.get(group) ?? 'None'}
            </StyledGroupLabel>
          ),
          options: groupOptions,
        })
      )

      if (noParentOptions.length > 0) {
        optionsToRender.push({
          label: (
            <StyledGroupLabel key='group-none'>
              Parent Org: None
            </StyledGroupLabel>
          ),
          options: noParentOptions,
        })
      }
    }

    return (
      <StyledSelectWrapper style={style}>
        <Select
          id={id}
          style={{ width: '100%', height: '100%' }}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          ref={ref}
          showSearch
          placeholder={placeholder}
          options={optionsToRender}
          optionFilterProp='value'
          filterOption={(input, option) => {
            if (!option || 'options' in option) {
              return false
            }
            const dataItem = data.find((item) => item.value === option.value)
            return (
              dataItem?.searchName
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            )
          }}
          virtual
          labelInValue={false}
          notFoundContent={notFoundContent}
        />
      </StyledSelectWrapper>
    )
  }
)

SalesforceSelect.displayName = 'SalesforceSelect'
